import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getAcquisition } from '../lib/acquisition';
import TurnstileWidget, { TURNSTILE_ENABLED } from '../components/TurnstileWidget';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next') || '/';
  const fromTryItFree = searchParams.get('signup') === '1';
  const [isSignUp, setIsSignUp] = useState(fromTryItFree);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [showResend, setShowResend] = useState(false);
  // Turnstile CAPTCHA token (single-use). Cleared + the widget reset after each
  // auth call. captchaResetKey bumps to force a fresh challenge.
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const resetCaptcha = () => { setCaptchaToken(''); setCaptchaResetKey(k => k + 1); };
  const captchaMissing = () => {
    if (TURNSTILE_ENABLED && !captchaToken) { setError('Please complete the verification below.'); setLoading(false); return true; }
    return false;
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Enter your email'); return; }
    setLoading(true); setError('');
    if (captchaMissing()) return;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin, captchaToken });
    resetCaptcha();
    if (error) setError(error.message);
    else setResetSent(true);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Enter email and password'); return; }
    if (isSignUp && password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (isSignUp && password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    if (captchaMissing()) return;
    try {
      if (isSignUp) {
        const redirectTo = window.location.origin + (nextUrl.startsWith('/') ? nextUrl : '/');
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name, acquisition: getAcquisition() }, emailRedirectTo: redirectTo, captchaToken },
        });
        if (error) throw error;
        if (!data.session) {
          setConfirmSent(true);
          setLoading(false);
          return;
        }
        window.location.href = nextUrl;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } });
        if (error) throw error;
        window.location.href = nextUrl;
      }
    } catch (err: any) {
      setError(err.message);
      if (err.message?.toLowerCase().includes('email not confirmed')) {
        setShowResend(true);
      }
    } finally {
      resetCaptcha();
    }
    setLoading(false);
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-logo">G</div>
        <h1>{forgotPassword ? 'Reset Password' : isSignUp && fromTryItFree ? 'Create Your Account' : isSignUp ? 'Create Account' : 'Sign In'}</h1>
        {!(isSignUp && fromTryItFree) && (
          <p className="auth-subtitle">{forgotPassword ? 'Enter your email and we\'ll send you a reset link' : isSignUp ? 'Start your coaching knowledge journey' : 'Access your coaching knowledge base'}</p>
        )}
        {error && <div className="auth-error">{error}</div>}
        {showResend && (
          <button
            className="auth-btn"
            style={{ width: '100%', background: 'var(--surface2)', color: 'var(--text)', marginBottom: 12 }}
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              setError('');
              if (captchaMissing()) return;
              const { error: resendErr } = await supabase.auth.resend({ type: 'signup', email, options: { captchaToken } });
              resetCaptcha();
              if (resendErr) setError(resendErr.message);
              else { setError('Confirmation email resent. Check your inbox and spam folder.'); setShowResend(false); }
              setLoading(false);
            }}
          >
            {loading ? 'Sending...' : 'Resend confirmation email'}
          </button>
        )}
        {!resetSent && (
          <TurnstileWidget onToken={setCaptchaToken} onExpire={() => setCaptchaToken('')} resetKey={captchaResetKey} />
        )}
        {forgotPassword ? (
          resetSent ? (
            <>
              <div className="success-msg">Check your email for a password reset link.</div>
              <div className="auth-toggle"><a onClick={() => { setForgotPassword(false); setResetSent(false); setError(''); }}>Back to sign in</a></div>
            </>
          ) : (
            <>
              <form onSubmit={handleForgotPassword}>
                <div className="field"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" /></div>
                <button className="auth-btn" type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
              </form>
              <div className="auth-toggle"><a onClick={() => { setForgotPassword(false); setError(''); }}>Back to sign in</a></div>
            </>
          )
        ) : confirmSent ? (
          <>
            <div className="success-msg">Check your email to confirm your account. Click the link we sent to complete signup. If you don't see it, check your spam or junk folder.</div>
            <button
              className="auth-btn"
              style={{ width: '100%', background: 'var(--surface2)', color: 'var(--text)', marginTop: 16 }}
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                setError('');
                if (captchaMissing()) return;
                const { error } = await supabase.auth.resend({ type: 'signup', email, options: { captchaToken } });
                resetCaptcha();
                if (error) setError(error.message);
                else setError('Confirmation email resent. Check your inbox and spam folder.');
                setLoading(false);
              }}
            >
              {loading ? 'Sending...' : 'Resend confirmation email'}
            </button>
            <div className="auth-toggle"><a onClick={() => { setConfirmSent(false); setError(''); }}>Back to sign in</a></div>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {isSignUp && <div className="field"><label>Full Name</label><input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" /></div>}
              <div className="field"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" /></div>
              <div className="field"><label>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={isSignUp ? 'Choose a password (min 6 chars)' : 'Your password'} /></div>
              {isSignUp && <div className="field"><label>Confirm Password</label><input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" /></div>}
              <button className="auth-btn" type="submit" disabled={loading}>{loading ? (isSignUp ? 'Creating...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign In')}</button>
            </form>
            {!isSignUp && <div className="auth-toggle"><a onClick={() => { setForgotPassword(true); setError(''); }}>Forgot password?</a></div>}
            <div className="auth-toggle">
              <span>{isSignUp ? 'Already have an account? ' : 'No account? '}</span>
              <a onClick={() => { setIsSignUp(!isSignUp); setError(''); }}>{isSignUp ? 'Sign in' : 'Sign up'}</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
